import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Checkbox,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
    TbCheck,
    TbChevronDown,
    TbChevronRight,
    TbX,
} from 'react-icons/tb';
import BulkActionBar, { BulkActionBarItem } from '../../../components/BulkActionBar';
import MfgPartNumberWithCopy from '../../../components/inventory/MfgPartNumberWithCopy';
import PurchaseOrderPdfButton from '../../../components/inventory/PurchaseOrderPdfButton';
import { formatPrice } from '../../../functions';
import { useGetPurchaseOrdersFeed } from '../../../hooks/inventory/useGetPurchaseOrdersFeed';
import { useSubmitPurchaseOrder } from '../../../hooks/inventory/useSubmitPurchaseOrder';
import {
    deliveryDaysLabel,
    PurchaseOrderEntry,
    PurchaseOrderPart,
    PurchaseOrderPartStatus,
    PurchaseOrderStatus,
    SubmitPurchaseOrderInput,
    SubmitPurchaseOrderPart,
} from '../../../types/inventory/purchaseOrders';

type DirtyAction = 'confirm' | 'reject';

type DirtyState = Record<string, Record<string, DirtyAction>>;
type DaysDraft = Record<string, Record<string, number>>;
type SelectedState = Record<string, Set<string>>;

const thStyle = {
    py: 2,
    px: 2,
    fontSize: 'xs',
    fontWeight: 'semibold' as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: 'gray.600',
    borderBottom: '1px solid',
    borderColor: 'gray.200',
    bg: 'gray.50',
    whiteSpace: 'nowrap' as const,
};

const tdStyle = {
    py: 1.5,
    px: 2,
    fontSize: 'xs',
    borderBottom: '1px solid',
    borderColor: 'gray.100',
};

function extractApiErrorMessage(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        return data?.message?.trim() || fallback;
    }
    if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        (error as { response?: { data?: { message?: string } } }).response?.data?.message
    ) {
        return String(
            (error as { response: { data: { message: string } } }).response.data.message,
        ).trim();
    }
    if (error instanceof Error && error.message.trim()) {
        return error.message.trim();
    }
    return fallback;
}

function ActionLabel({
    status,
}: {
    status: PurchaseOrderPartStatus | DirtyAction | 'pending';
}) {
    if (status === 'confirm' || status === 'confirmed') {
        return (
            <HStack spacing={1} color="green.600">
                <Icon as={TbCheck as React.ElementType} boxSize={3.5} />
                <Text fontWeight="medium">Confirm</Text>
            </HStack>
        );
    }
    if (status === 'reject' || status === 'rejected') {
        return (
            <HStack spacing={1} color="red.600">
                <Icon as={TbX as React.ElementType} boxSize={3.5} />
                <Text fontWeight="medium">Reject</Text>
            </HStack>
        );
    }
    return (
        <Text color="gray.500" fontWeight="medium">
            Select…
        </Text>
    );
}

interface PartActionCellProps {
    part: PurchaseOrderPart;
    dirty: DirtyAction | undefined;
    onChange: (action: DirtyAction | undefined) => void;
}

const PartActionCell = ({ part, dirty, onChange }: PartActionCellProps) => {
    if (part.status !== 'pending') {
        return <ActionLabel status={part.status} />;
    }

    return (
        <Menu>
            <MenuButton
                as={Button}
                size="xs"
                variant="ghost"
                px={1}
                h="auto"
                minW={0}
                fontWeight="medium"
                rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} color="gray.400" />}
            >
                <ActionLabel status={dirty ?? 'pending'} />
            </MenuButton>
            <MenuList minW="140px" fontSize="sm" py={1}>
                <MenuItem onClick={() => onChange(undefined)}>
                    <Text color="gray.500" fontWeight="medium">
                        Select…
                    </Text>
                </MenuItem>
                <MenuItem
                    icon={<Icon as={TbCheck as React.ElementType} boxSize={3.5} color="green.600" />}
                    color="green.600"
                    fontWeight="medium"
                    onClick={() => onChange('confirm')}
                >
                    Confirm
                </MenuItem>
                <MenuItem
                    icon={<Icon as={TbX as React.ElementType} boxSize={3.5} color="red.600" />}
                    color="red.600"
                    fontWeight="medium"
                    onClick={() => onChange('reject')}
                >
                    Reject
                </MenuItem>
            </MenuList>
        </Menu>
    );
};

type EtaPreset = 'same' | 'next' | 'custom';

function presetFromDays(days: number): EtaPreset {
    if (days === 0) return 'same';
    if (days === 1) return 'next';
    return 'custom';
}

interface EtaCellProps {
    value: number;
    editable: boolean;
    onChange: (days: number) => void;
}

const EtaOption = ({
    label,
    isSelected,
    onClick,
}: {
    label: string;
    isSelected: boolean;
    onClick: () => void;
}) => (
    <Box
        px={3}
        py={2}
        mx={2}
        my={0.5}
        cursor="pointer"
        bg={isSelected ? '#0071E3' : 'transparent'}
        borderRadius="lg"
        _hover={{ bg: isSelected ? '#0071E3' : 'gray.100' }}
        onClick={onClick}
    >
        <HStack justify="space-between">
            <Text
                fontSize="sm"
                color={isSelected ? 'white' : 'gray.700'}
                fontWeight="medium"
            >
                {label}
            </Text>
            {isSelected ? <CheckIcon color="white" boxSize={3} /> : null}
        </HStack>
    </Box>
);

const EtaCell = ({ value, editable, onChange }: EtaCellProps) => {
    const preset = presetFromDays(value);
    const [menuOpen, setMenuOpen] = useState(false);
    const [customDraft, setCustomDraft] = useState(
        preset === 'custom' ? String(value) : '2',
    );
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (preset === 'custom') setCustomDraft(String(value));
    }, [value, preset]);

    useEffect(() => {
        if (!(menuOpen && preset === 'custom')) return;
        const t = setTimeout(() => inputRef.current?.focus(), 0);
        return () => clearTimeout(t);
    }, [menuOpen, preset]);

    if (!editable) {
        return <Text>{deliveryDaysLabel(value)}</Text>;
    }

    const commitCustom = (raw: string) => {
        const digits = raw.replace(/\D/g, '');
        const parsed = parseInt(digits, 10);
        if (!Number.isFinite(parsed) || parsed < 2) {
            onChange(2);
            setCustomDraft('2');
            return;
        }
        onChange(parsed);
        setCustomDraft(String(parsed));
    };

    return (
        <Menu
            isOpen={menuOpen}
            onOpen={() => setMenuOpen(true)}
            onClose={() => {
                if (preset === 'custom') {
                    commitCustom(customDraft);
                }
                setMenuOpen(false);
            }}
            closeOnSelect={false}
        >
            <MenuButton
                as={Button}
                size="xs"
                variant="ghost"
                px={1}
                h="auto"
                minW={0}
                fontWeight="medium"
                rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3} color="gray.400" />}
            >
                {deliveryDaysLabel(value)}
            </MenuButton>
            <MenuList minW="180px" py={1} px={0}>
                <VStack align="stretch" spacing={0} py={1}>
                    <EtaOption
                        label="Same Day"
                        isSelected={preset === 'same'}
                        onClick={() => {
                            onChange(0);
                            setMenuOpen(false);
                        }}
                    />
                    <EtaOption
                        label="Next Day"
                        isSelected={preset === 'next'}
                        onClick={() => {
                            onChange(1);
                            setMenuOpen(false);
                        }}
                    />
                    <EtaOption
                        label="Custom"
                        isSelected={preset === 'custom'}
                        onClick={() => {
                            const next = value >= 2 ? value : 2;
                            onChange(next);
                            setCustomDraft(String(next));
                        }}
                    />
                    {preset === 'custom' ? (
                        <Box px={3} py={2}>
                            <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>
                                DAYS
                            </Text>
                            <Input
                                ref={inputRef}
                                type="number"
                                size="sm"
                                value={customDraft}
                                onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, '');
                                    setCustomDraft(digits);
                                }}
                                onBlur={() => commitCustom(customDraft)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        commitCustom(customDraft);
                                        setMenuOpen(false);
                                    }
                                    e.stopPropagation();
                                }}
                                onClick={(e) => e.stopPropagation()}
                                bg="white"
                                borderColor="gray.200"
                                fontSize="sm"
                                inputMode="numeric"
                                min={2}
                                placeholder="2"
                            />
                        </Box>
                    ) : null}
                </VStack>
            </MenuList>
        </Menu>
    );
};

interface PurchaseOrderGroupProps {
    request: PurchaseOrderEntry;
    initiallyExpanded: boolean;
    highlight: boolean;
    dirtyActions: Record<string, DirtyAction>;
    daysDrafts: Record<string, number>;
    selectedPartIds: Set<string>;
    onSetPartAction: (partId: string, action: DirtyAction | undefined) => void;
    onSetDays: (partId: string, days: number) => void;
    onTogglePartSelected: (partId: string, checked: boolean) => void;
    onToggleSelectAllPending: (checked: boolean) => void;
}

const PurchaseOrderGroup = ({
    request,
    initiallyExpanded,
    highlight,
    dirtyActions,
    daysDrafts,
    selectedPartIds,
    onSetPartAction,
    onSetDays,
    onTogglePartSelected,
    onToggleSelectAllPending,
}: PurchaseOrderGroupProps) => {
    const [expanded, setExpanded] = useState(initiallyExpanded);
    const rowRef = useRef<HTMLTableRowElement>(null);

    useEffect(() => {
        if (highlight && rowRef.current) {
            rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [highlight]);

    const pendingParts = useMemo(
        () => request.parts.filter((part) => part.status === 'pending'),
        [request.parts],
    );

    const allPendingSelected =
        pendingParts.length > 0 && pendingParts.every((p) => selectedPartIds.has(p.partId));
    const somePendingSelected = pendingParts.some((p) => selectedPartIds.has(p.partId));

    const groupBg = highlight ? 'blue.50' : expanded ? 'gray.50' : 'white';

    return (
        <Fragment>
            <Tr ref={rowRef} bg={groupBg} _hover={{ bg: highlight ? 'blue.50' : 'gray.50' }}>
                <Td {...tdStyle} colSpan={5}>
                    <HStack spacing={2} minW={0}>
                        <IconButton
                            aria-label={expanded ? 'Collapse RO' : 'Expand RO'}
                            icon={
                                <Icon
                                    as={(expanded ? TbChevronDown : TbChevronRight) as React.ElementType}
                                    boxSize={4}
                                />
                            }
                            size="xs"
                            variant="ghost"
                            color="gray.600"
                            onClick={() => setExpanded((prev) => !prev)}
                        />
                        {pendingParts.length > 0 ? (
                            <Checkbox
                                size="sm"
                                isChecked={allPendingSelected}
                                isIndeterminate={somePendingSelected && !allPendingSelected}
                                onChange={(e) => onToggleSelectAllPending(e.target.checked)}
                                aria-label="Select all pending parts for this purchase order"
                            />
                        ) : null}
                        <Text fontWeight="semibold" color="gray.800">
                            RO {request.roNumber}
                        </Text>
                        {request.invoiceNumber ? (
                            <Text color="gray.500">PO {request.invoiceNumber}</Text>
                        ) : null}
                        <Text color="gray.500" noOfLines={1}>
                            {request.sourceLocationName} → {request.destinationLocationName}
                            {request.requestorEmail ? ` · ${request.requestorEmail}` : ''}
                        </Text>
                    </HStack>
                </Td>
                <Td {...tdStyle} whiteSpace="nowrap">
                    {request.purchaseOrderId && request.invoiceNumber ? (
                        <PurchaseOrderPdfButton
                            purchaseOrderId={request.purchaseOrderId}
                            invoiceNumber={request.invoiceNumber}
                        />
                    ) : null}
                </Td>
            </Tr>
            {expanded
                ? request.parts.map((part) => (
                    <Tr key={part.partId} bg="white" _hover={{ bg: 'gray.50' }}>
                        <Td {...tdStyle} pl={10}>
                            <HStack spacing={2}>
                                {part.status === 'pending' ? (
                                    <Checkbox
                                        size="sm"
                                        isChecked={selectedPartIds.has(part.partId)}
                                        onChange={(e) =>
                                            onTogglePartSelected(part.partId, e.target.checked)
                                        }
                                        aria-label={`Select ${part.name}`}
                                    />
                                ) : (
                                    <Box w="16px" />
                                )}
                                <Text fontWeight="medium">{part.name}</Text>
                            </HStack>
                        </Td>
                        <Td {...tdStyle}>
                            <MfgPartNumberWithCopy
                                partNumber={part.partNumber}
                                color="gray.600"
                                fontWeight="normal"
                            />
                        </Td>
                        <Td {...tdStyle} isNumeric>
                            {part.quantity}
                        </Td>
                        <Td {...tdStyle} isNumeric>
                            {formatPrice(part.buyPrice)}
                        </Td>
                        <Td {...tdStyle}>
                            <EtaCell
                                value={daysDrafts[part.partId] ?? part.deliveryDays}
                                editable={part.status === 'pending'}
                                onChange={(days) => onSetDays(part.partId, days)}
                            />
                        </Td>
                        <Td {...tdStyle}>
                            <PartActionCell
                                part={part}
                                dirty={dirtyActions[part.partId]}
                                onChange={(action) => onSetPartAction(part.partId, action)}
                            />
                        </Td>
                    </Tr>
                ))
                : null}
        </Fragment>
    );
};

interface InventoryPurchaseOrdersPanelProps {
    locationIds: Array<string> | 'all';
    purchaseOrderId?: string | null;
    searchQuery?: string;
}

export function InventoryPurchaseOrdersPanel({
    locationIds,
    purchaseOrderId = null,
    searchQuery = '',
}: InventoryPurchaseOrdersPanelProps) {
    const toast = useToast();
    const submitMutation = useSubmitPurchaseOrder();
    const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | 'all'>('all');
    const [useAllForFocus, setUseAllForFocus] = useState(!!purchaseOrderId);
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery.trim());
    const [dirtyByOrder, setDirtyByOrder] = useState<DirtyState>({});
    const [daysByOrder, setDaysByOrder] = useState<DaysDraft>({});
    const [selectedByOrder, setSelectedByOrder] = useState<SelectedState>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 250);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        if (purchaseOrderId) setUseAllForFocus(true);
    }, [purchaseOrderId]);

    const feedStatusFilter = useAllForFocus && purchaseOrderId ? 'all' : statusFilter;

    const feed = useGetPurchaseOrdersFeed({
        statusFilter: feedStatusFilter,
        locationIds,
        search: debouncedSearch || undefined,
        purchaseOrderId: purchaseOrderId || undefined,
        limit: 50,
    });

    // Feed hook already collapses cost-lot siblings into one row per partId.
    const requests = useMemo(() => {
        const rows = feed.data?.requests ?? [];
        const partsForTab = (parts: PurchaseOrderPart[]) => {
            if (statusFilter === 'all') return parts;
            if (statusFilter === 'pending') {
                return parts.filter((part) => part.status === 'pending');
            }
            return parts.filter(
                (part) => part.status === 'confirmed' || part.status === 'rejected',
            );
        };

        // API returns part subsets for pending/completed; filter again so `all`
        // (deep-link focus) also matches the active tab.
        const mapped = rows.map((row) => ({
            ...row,
            parts: partsForTab(row.parts),
        }));

        const withParts = mapped.filter((row) => row.parts.length > 0);
        if (!(useAllForFocus && purchaseOrderId)) {
            return withParts;
        }

        const focused = mapped.find((row) => row.purchaseOrderId === purchaseOrderId);
        if (focused && focused.parts.length === 0) {
            return withParts;
        }
        if (focused && !withParts.some((row) => row.purchaseOrderId === purchaseOrderId)) {
            return [focused, ...withParts];
        }
        return withParts;
    }, [feed.data?.requests, statusFilter, purchaseOrderId, useAllForFocus]);

    useEffect(() => {
        // Switching tabs clears selection / dirty draft scope for the other tab.
        setSelectedByOrder({});
    }, [statusFilter]);

    useEffect(() => {
        setDaysByOrder((prev) => {
            const next: DaysDraft = { ...prev };
            for (const request of feed.data?.requests ?? []) {
                const existing = next[request.purchaseOrderId] ?? {};
                const merged: Record<string, number> = { ...existing };
                for (const part of request.parts) {
                    if (merged[part.partId] === undefined) {
                        merged[part.partId] = part.deliveryDays;
                    }
                }
                next[request.purchaseOrderId] = merged;
            }
            return next;
        });
    }, [feed.data?.requests]);

    const dirtyPartCount = useMemo(() => {
        let count = 0;
        for (const parts of Object.values(dirtyByOrder)) {
            count += Object.keys(parts).length;
        }
        return count;
    }, [dirtyByOrder]);

    const canSubmit = useMemo(() => {
        if (dirtyPartCount === 0) return false;
        for (const [orderId, actions] of Object.entries(dirtyByOrder)) {
            const days = daysByOrder[orderId] ?? {};
            for (const [partId, action] of Object.entries(actions)) {
                if (action !== 'confirm') continue;
                const d = days[partId];
                if (!Number.isInteger(d) || d < 0) return false;
            }
        }
        return true;
    }, [dirtyByOrder, daysByOrder, dirtyPartCount]);

    const setPartAction = (
        purchaseOrderIdKey: string,
        partId: string,
        action: DirtyAction | undefined,
    ) => {
        setDirtyByOrder((prev) => {
            const orderDirty = { ...(prev[purchaseOrderIdKey] ?? {}) };
            if (!action) delete orderDirty[partId];
            else orderDirty[partId] = action;
            const next = { ...prev };
            if (Object.keys(orderDirty).length === 0) delete next[purchaseOrderIdKey];
            else next[purchaseOrderIdKey] = orderDirty;
            return next;
        });
    };

    const setDays = (purchaseOrderIdKey: string, partId: string, days: number) => {
        setDaysByOrder((prev) => ({
            ...prev,
            [purchaseOrderIdKey]: {
                ...(prev[purchaseOrderIdKey] ?? {}),
                [partId]: days,
            },
        }));
    };

    const togglePartSelected = (
        purchaseOrderIdKey: string,
        partId: string,
        checked: boolean,
    ) => {
        setSelectedByOrder((prev) => {
            const nextSet = new Set(prev[purchaseOrderIdKey] ?? []);
            if (checked) nextSet.add(partId);
            else nextSet.delete(partId);
            const next = { ...prev };
            if (nextSet.size === 0) delete next[purchaseOrderIdKey];
            else next[purchaseOrderIdKey] = nextSet;
            return next;
        });
    };

    const toggleSelectAllPending = (request: PurchaseOrderEntry, checked: boolean) => {
        const pendingIds = request.parts
            .filter((p) => p.status === 'pending')
            .map((p) => p.partId);
        setSelectedByOrder((prev) => {
            const next = { ...prev };
            if (!checked) {
                delete next[request.purchaseOrderId];
                return next;
            }
            next[request.purchaseOrderId] = new Set(pendingIds);
            return next;
        });
    };

    const selectedTotal = useMemo(() => {
        let count = 0;
        for (const set of Object.values(selectedByOrder)) {
            count += set.size;
        }
        return count;
    }, [selectedByOrder]);

    const clearSelection = () => setSelectedByOrder({});

    const applyBulkAction = (action: DirtyAction) => {
        setDirtyByOrder((prev) => {
            const next = { ...prev };
            for (const request of requests) {
                const selected = selectedByOrder[request.purchaseOrderId];
                if (!selected?.size) continue;
                const orderDirty = { ...(next[request.purchaseOrderId] ?? {}) };
                for (const part of request.parts) {
                    if (part.status !== 'pending') continue;
                    if (!selected.has(part.partId)) continue;
                    orderDirty[part.partId] = action;
                }
                next[request.purchaseOrderId] = orderDirty;
            }
            return next;
        });
    };

    const bulkBarActions: BulkActionBarItem[] = [
        {
            id: 'confirm',
            label: 'Confirm',
            icon: TbCheck as React.ElementType,
            success: true,
        },
        {
            id: 'reject',
            label: 'Reject',
            icon: TbX as React.ElementType,
            danger: true,
        },
    ];

    const handleGlobalSubmit = async () => {
        if (!canSubmit) {
            toast({
                title: 'ETA required',
                description: 'Set delivery days for every part marked Confirm before submitting.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const payloads: SubmitPurchaseOrderInput[] = Object.entries(dirtyByOrder).map(
            ([orderId, actions]) => {
                const days = daysByOrder[orderId] ?? {};
                const parts: SubmitPurchaseOrderPart[] = Object.entries(actions).map(
                    ([partId, action]) => {
                        if (action === 'confirm') {
                            return {
                                partId,
                                action: 'confirm',
                                deliveryDays: days[partId],
                            };
                        }
                        return { partId, action: 'reject' };
                    },
                );
                return { purchaseOrderId: orderId, parts };
            },
        );

        setIsSubmitting(true);
        try {
            for (const payload of payloads) {
                await submitMutation.mutateAsync(payload);
            }
            setDirtyByOrder({});
            setSelectedByOrder({});
            toast({
                title: 'Purchase orders updated',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to submit purchase orders',
                description: extractApiErrorMessage(error, 'Please try again.'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const tabs: Array<{ mode: PurchaseOrderStatus | 'all'; label: string }> = [
        { mode: 'all', label: 'All' },
        { mode: 'pending', label: 'Pending' },
        { mode: 'completed', label: 'Completed' },
    ];

    const showSubmit = statusFilter === 'pending' || statusFilter === 'all';

    return (
        <VStack spacing={0} align="stretch" h="full" px={6} pt={2} pb={4}>
            <Flex
                flexShrink={0}
                w="100%"
                align="flex-end"
                justify="space-between"
                borderBottom="1.5px solid"
                borderColor="gray.200"
                mb={3}
                pb={0}
                gap={3}
                flexWrap="wrap"
            >
                <Flex gap={0} flexShrink={0}>
                    {tabs.map(({ mode, label }) => (
                        <Box
                            key={mode}
                            as="button"
                            type="button"
                            onClick={() => {
                                setStatusFilter(mode);
                                if (!purchaseOrderId) setUseAllForFocus(false);
                            }}
                            fontWeight="medium"
                            fontSize="sm"
                            color={statusFilter === mode ? 'gray.800' : 'gray.500'}
                            bg="transparent"
                            border="none"
                            borderBottom="1.5px solid"
                            borderColor={statusFilter === mode ? 'gray.500' : 'transparent'}
                            mb="-1.5px"
                            py={1.5}
                            px={3}
                            cursor="pointer"
                            transition="all 0.15s"
                            _hover={{
                                color: statusFilter === mode ? 'gray.800' : 'gray.700',
                            }}
                        >
                            {label}
                        </Box>
                    ))}
                </Flex>
                {showSubmit ? (
                    <Box mb={1}>
                        <Button
                            size="sm"
                            h="32px"
                            px={4}
                            bg="#0071E3"
                            color="white"
                            fontWeight="medium"
                            fontSize="sm"
                            _hover={{ bg: '#3544a0' }}
                            onClick={handleGlobalSubmit}
                            isDisabled={!canSubmit || isSubmitting}
                            isLoading={isSubmitting}
                            loadingText="Submitting..."
                        >
                            Submit
                        </Button>
                    </Box>
                ) : null}
            </Flex>

            {feed.isLoading ? (
                <Flex justify="center" align="center" py={16}>
                    <Spinner color="#0071E3" />
                </Flex>
            ) : feed.isError ? (
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                        Could not load purchase orders. Please try again.
                    </AlertDescription>
                </Alert>
            ) : requests.length === 0 ? (
                <Flex justify="center" align="center" py={16}>
                    <Text color="gray.500" fontSize="sm">
                        {statusFilter === 'pending'
                            ? 'No pending purchase orders'
                            : statusFilter === 'completed'
                              ? 'No completed purchase orders'
                              : 'No purchase orders'}
                    </Text>
                </Flex>
            ) : (
                <Box position="relative" overflowX="auto" overflowY="auto" flex={1} minH={0} borderWidth="1px" borderColor="gray.200" borderRadius="md">
                    <Table
                        variant="simple"
                        size="sm"
                        w="100%"
                        sx={{ tableLayout: 'fixed' }}
                    >
                        <Thead position="sticky" top={0} zIndex={1}>
                            <Tr>
                                <Th {...thStyle}>Part</Th>
                                <Th {...thStyle} w="18%">
                                    Mfg. Part Number
                                </Th>
                                <Th {...thStyle} isNumeric w="10%">
                                    Quantity
                                </Th>
                                <Th {...thStyle} isNumeric w="11%">
                                    Buy Price
                                </Th>
                                <Th {...thStyle} w="9%">
                                    ETA
                                </Th>
                                <Th {...thStyle} w="10%">
                                    Action
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {requests.map((request) => (
                                <PurchaseOrderGroup
                                    key={request.purchaseOrderId}
                                    request={request}
                                    initiallyExpanded={
                                        !purchaseOrderId ||
                                        request.purchaseOrderId === purchaseOrderId
                                    }
                                    highlight={
                                        !!purchaseOrderId &&
                                        request.purchaseOrderId === purchaseOrderId
                                    }
                                    dirtyActions={dirtyByOrder[request.purchaseOrderId] ?? {}}
                                    daysDrafts={daysByOrder[request.purchaseOrderId] ?? {}}
                                    selectedPartIds={
                                        selectedByOrder[request.purchaseOrderId] ?? new Set()
                                    }
                                    onSetPartAction={(partId, action) =>
                                        setPartAction(request.purchaseOrderId, partId, action)
                                    }
                                    onSetDays={(partId, days) =>
                                        setDays(request.purchaseOrderId, partId, days)
                                    }
                                    onTogglePartSelected={(partId, checked) =>
                                        togglePartSelected(
                                            request.purchaseOrderId,
                                            partId,
                                            checked,
                                        )
                                    }
                                    onToggleSelectAllPending={(checked) =>
                                        toggleSelectAllPending(request, checked)
                                    }
                                />
                            ))}
                        </Tbody>
                    </Table>
                    <BulkActionBar
                        selectedCount={selectedTotal}
                        actions={bulkBarActions}
                        onClearSelection={clearSelection}
                        onAction={(actionId) => {
                            if (actionId === 'confirm' || actionId === 'reject') {
                                applyBulkAction(actionId);
                            }
                        }}
                    />
                </Box>
            )}
        </VStack>
    );
}
